import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { GraduationCap, Plus, Loader2, Download } from 'lucide-react';
import { usePagePermissions } from '../hooks/usePagePermissions';
import { useTrainingLog } from '../hooks/useTrainingLog';
import { TrainingSummary } from '../components/training-log/TrainingSummary';
import { TrainingList } from '../components/training-log/TrainingList';
import { TrainingForm } from '../components/training-log/TrainingForm';
import { ReminderConfig } from '../components/training-log/ReminderConfig';
import { TrainingMonthlyStatus } from '../components/training-log/TrainingMonthlyStatus';
import { TrainingNoTrainingBanner } from '../components/training-log/TrainingNoTrainingBanner';
import { ConfirmModal } from '../components/ui/ConfirmModal';
import { TrainingDetailModal } from '../components/training-log/TrainingDetailModal';
import type { Training, CreateTrainingData } from '../services/trainingLogApi';
import { downloadCsv, hoursPerEmployeeCsv, trainingsCsv } from '../utils/trainingLogExport';
import { parseConfirmMonth } from '../utils/noTrainingConfirm';

export default function TrainingLogPage() {
    const { canEdit, isLoading: permLoading } = usePagePermissions('/training-log');
    const [searchParams] = useSearchParams();
    // Mail link: /training-log?confirmNoTraining=YYYY-MM. Only a valid month shows the banner.
    const confirmMonth = parseConfirmMonth(searchParams.get('confirmNoTraining'));
    const {
        trainings,
        teamTrainings,
        allTrainings,
        summary,
        teamSummary,
        allSummary,
        selectedYear,
        setSelectedYear,
        loading,
        error,
        activeTab,
        setActiveTab,
        createTraining,
        updateTraining,
        deleteTraining,
    } = useTrainingLog();

    const [showForm, setShowForm] = useState(false);
    const [editingTraining, setEditingTraining] = useState<Training | null>(null);
    const [deletingTraining, setDeletingTraining] = useState<Training | null>(null);
    const [viewingTraining, setViewingTraining] = useState<Training | null>(null);
    // Full-page spinner only for the first load. Later reloads (year, save, delete) keep the children
    // mounted, so the month and filter in the overview and the banner state do not jump back.
    const [hasLoaded, setHasLoaded] = useState(false);
    useEffect(() => { if (!loading) setHasLoaded(true); }, [loading]);
    // Bumped after a save or delete: the banner reads has-training again (no remount).
    const [bannerRefresh, setBannerRefresh] = useState(0);

    if (permLoading || (loading && !hasLoaded)) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
        );
    }

    const handleSubmit = async (data: CreateTrainingData) => {
        if (editingTraining) {
            await updateTraining(editingTraining.Id, data);
        } else {
            await createTraining(data);
        }
        setBannerRefresh(n => n + 1);
    };

    const handleEdit = (training: Training) => {
        setEditingTraining(training);
        setShowForm(true);
    };

    const handleDelete = async () => {
        if (deletingTraining) {
            await deleteTraining(deletingTraining.Id);
            setDeletingTraining(null);
            setBannerRefresh(n => n + 1);
        }
    };

    const handleCloseForm = () => {
        setShowForm(false);
        setEditingTraining(null);
    };

    const isManager = summary?.isManager ?? false;
    // Role HR or Admin, decided by the API; the server answers 403 to anyone else.
    const canSeeAll = summary?.canSeeAll ?? false;
    // Role HR may correct anybody's training; the server checks the role again.
    const canEditAll = canEdit && (summary?.canEditAll ?? false);
    const showYearFilter = activeTab !== 'settings';
    // Year buttons are scope-specific so the team tab only offers years
    // where the team actually has training records.
    const availableYears = activeTab === 'team'
        ? teamSummary?.availableYears ?? []
        : activeTab === 'all'
            ? allSummary?.availableYears ?? []
            : summary?.availableYears ?? [];

    const exportAll = (kind: 'trainings' | 'hours') => {
        const period = selectedYear ?? 'all-years';
        if (kind === 'trainings') downloadCsv(trainingsCsv(allTrainings), `training-log-all-employees-${period}.csv`);
        else downloadCsv(hoursPerEmployeeCsv(allTrainings), `training-log-hours-per-employee-${period}.csv`);
    };

    return (
        <div className="p-6 max-w-7xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <GraduationCap className="w-7 h-7 text-blue-600" />
                    <h1 className="text-2xl font-bold text-gray-900">Training Log</h1>
                    {loading && (
                        <span className="inline-flex items-center gap-1.5 text-xs text-gray-500" role="status">
                            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Updating...
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-3">
                    {/* Year Filter */}
                    {showYearFilter && availableYears.length > 0 && (
                        <div className="flex items-center gap-2">
                            <label className="text-sm text-gray-600">Year:</label>
                            <div className="flex gap-1">
                                {availableYears.map(year => (
                                    <button
                                        key={year}
                                        onClick={() => setSelectedYear(year)}
                                        className={`px-3 py-1.5 rounded text-sm font-medium border transition-colors ${
                                            selectedYear === year
                                                ? 'bg-blue-600 text-white border-blue-600'
                                                : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400'
                                        }`}
                                    >
                                        {year}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                    {canEdit && showYearFilter && (
                        <button
                            onClick={() => { setEditingTraining(null); setShowForm(true); }}
                            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                        >
                            <Plus className="w-4 h-4" />
                            Add Training
                        </button>
                    )}
                </div>
            </div>

            {error && (
                <div className="bg-red-50 text-red-700 text-sm px-4 py-3 rounded-lg border border-red-200 mb-6">
                    {error}
                </div>
            )}

            {confirmMonth && (
                <TrainingNoTrainingBanner
                    month={confirmMonth}
                    onLogTraining={() => { setEditingTraining(null); setShowForm(true); }}
                    refreshKey={bannerRefresh}
                />
            )}

            {/* Tabs */}
            <div className="flex gap-1 mb-6 bg-gray-100 rounded-lg p-1 w-fit">
                <button
                    onClick={() => setActiveTab('own')}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                        activeTab === 'own'
                            ? 'bg-white text-gray-900 shadow-sm'
                            : 'text-gray-600 hover:text-gray-900'
                    }`}
                >
                    My Trainings
                </button>
                {isManager && (
                    <button
                        onClick={() => setActiveTab('team')}
                        className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                            activeTab === 'team'
                                ? 'bg-white text-gray-900 shadow-sm'
                                : 'text-gray-600 hover:text-gray-900'
                        }`}
                    >
                        Team Overview
                    </button>
                )}
                {canSeeAll && (
                    <button
                        onClick={() => setActiveTab('all')}
                        className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                            activeTab === 'all'
                                ? 'bg-white text-gray-900 shadow-sm'
                                : 'text-gray-600 hover:text-gray-900'
                        }`}
                    >
                        All Employees
                    </button>
                )}
                {canSeeAll && (
                    <button
                        onClick={() => setActiveTab('settings')}
                        className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                            activeTab === 'settings'
                                ? 'bg-white text-gray-900 shadow-sm'
                                : 'text-gray-600 hover:text-gray-900'
                        }`}
                    >
                        Settings
                    </button>
                )}
            </div>

            {/* Content based on active tab */}
            {activeTab === 'own' && (
                <>
                    <TrainingSummary summary={summary} />
                    <TrainingList
                        trainings={trainings}
                        canEdit={canEdit}
                        onEdit={handleEdit}
                        onDelete={setDeletingTraining}
                    />
                </>
            )}
            {activeTab === 'team' && (
                <>
                    <TrainingSummary summary={teamSummary} showEmployeeCount />
                    <TrainingList
                        trainings={teamTrainings}
                        canEdit={false}
                        onEdit={() => {}}
                        onDelete={() => {}}
                        onRowClick={setViewingTraining}
                        showEmployee
                    />
                </>
            )}
            {activeTab === 'all' && canSeeAll && (
                <>
                    <TrainingSummary summary={allSummary} showEmployeeCount />
                    <TrainingMonthlyStatus />
                    <div className="flex justify-end gap-2 mb-3">
                        <button
                            onClick={() => exportAll('hours')}
                            disabled={allTrainings.length === 0}
                            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:border-blue-400 disabled:opacity-50"
                        >
                            <Download className="w-4 h-4" />
                            Hours per employee (CSV)
                        </button>
                        <button
                            onClick={() => exportAll('trainings')}
                            disabled={allTrainings.length === 0}
                            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:border-blue-400 disabled:opacity-50"
                        >
                            <Download className="w-4 h-4" />
                            All trainings (CSV)
                        </button>
                    </div>
                    <TrainingList
                        trainings={allTrainings}
                        canEdit={canEditAll}
                        onEdit={handleEdit}
                        onDelete={setDeletingTraining}
                        onRowClick={setViewingTraining}
                        showEmployee
                    />
                </>
            )}
            {activeTab === 'settings' && canSeeAll && (
                <ReminderConfig />
            )}

            {/* Form Modal */}
            {showForm && (
                <TrainingForm
                    training={editingTraining}
                    onSubmit={handleSubmit}
                    onClose={handleCloseForm}
                />
            )}

            {/* Detail Modal (Team and All views) */}
            {viewingTraining && (
                <TrainingDetailModal
                    training={viewingTraining}
                    onClose={() => setViewingTraining(null)}
                />
            )}

            {/* Delete Confirm Modal */}
            {deletingTraining && (
                <ConfirmModal
                    title="Delete Training"
                    message={`Are you sure you want to delete "${deletingTraining.CourseTopic}"${
                        deletingTraining.EmployeeName ? ` of ${deletingTraining.EmployeeName}` : ''
                    }? This action cannot be undone.`}
                    confirmLabel="Delete"
                    onConfirm={handleDelete}
                    onCancel={() => setDeletingTraining(null)}
                />
            )}
        </div>
    );
}
